import { faPlus } from "@fortawesome/free-solid-svg-icons";
import { SearchableTable } from "@fyrstain/fhir-front-library";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  SupportedActorResource,
  SupportedActorType,
} from "../../services/fhir/fhir.types";
import { mapActorResource } from "../../services/fhir/fhir.utils";
import { ActorPickerKind, pickerActorTypes } from "../../services/scheduling/AppointmentPlanning.types";
import styles from "./AppointmentPlanner.module.css";

interface ActorPickerProps {
  kind: ActorPickerKind;
  serverUrl: string;
  onAdd: (actorType: SupportedActorType, id: string) => void;
  onClose: () => void;
  onError: () => void;
}

const resourceTypeByActorType: Record<SupportedActorType, string> = {
  patient: "Patient",
  practitioner: "Practitioner",
  location: "Location",
  device: "Device",
};

const searchParameterByActorType: Record<SupportedActorType, string> = {
  patient: "name",
  practitioner: "name",
  location: "name",
  device: "device-name",
};

export default function ActorPicker({ kind, serverUrl, onAdd, onClose, onError }: ActorPickerProps) {
  const { t } = useTranslation();
  const allowedTypes = pickerActorTypes[kind];
  const [actorType, setActorType] = useState<SupportedActorType>(allowedTypes[0]);

  return (
    <section className={styles.picker} aria-label={t(`planner.picker.${kind}`)}>
      <header>
        <div>
          <span>{t("planner.picker.eyebrow")}</span>
          <h2>{t(`planner.picker.${kind}`)}</h2>
        </div>
        <button type="button" onClick={onClose} aria-label={t("common.close")}>×</button>
      </header>

      {allowedTypes.length > 1 && (
        <div className={styles.pickerTabs} role="tablist">
          {allowedTypes.map((candidate) => (
            <button
              key={candidate}
              type="button"
              role="tab"
              aria-selected={candidate === actorType}
              className={candidate === actorType ? styles.pickerTabActive : styles.pickerTab}
              onClick={() => setActorType(candidate)}
            >
              {t(`calendar.actorTypes.${candidate}`)}
            </button>
          ))}
        </div>
      )}

      <SearchableTable
        key={actorType}
        searchCriteriaProperties={{
          title: t("planner.picker.search"),
          submitButtonLabel: t("directory.search"),
          resetButtonLabel: t("directory.clearSearch"),
          inputs: [
            {
              label: t(`calendar.actorTypes.${actorType}`),
              placeholder: t(`directory.placeholders.${actorType}`),
              type: "text",
              searchParamsName: searchParameterByActorType[actorType],
            },
          ],
        }}
        paginatedTableProperties={{
          columns: [
            {
              header: t("planner.picker.name"),
              dataField: "display",
              width: "65%",
            },
            {
              header: t("planner.picker.details"),
              dataField: "subtitle",
              width: "30%",
            },
          ],
          action: [
            {
              icon: faPlus,
              onClick: (id: string) => onAdd(actorType, id),
            },
          ],
          mapResourceToData: (resource: unknown) => {
            const actor = mapActorResource(resource as SupportedActorResource);
            return {
              id: actor.id,
              display: actor.display,
              subtitle: actor.subtitle || t(`calendar.actorTypes.${actor.type}`),
            };
          },
          searchProperties: {
            serverUrl,
            resourceType: resourceTypeByActorType[actorType],
          },
          onError,
        }}
      />
    </section>
  );
}
